"use client"

import { Check, Copy, Share2 } from "lucide-react"
import { useEffect, useState } from "react"
import ReactSelect from "react-select"
import type { StylesConfig } from "react-select"

import { cn } from "@/lib/utils"
import { COUNTRIES } from "@/lib/countries"
import { sendWelcomeEmail } from "@/services/emailService"
import { completeWaitlistDetails, joinWaitlist } from "@/services/waitlist-api"

import { GENRES } from "./content"

/** Details-step option lists (ported from the original WaitlistFormInline). */
const GENRE_OPTIONS = [
  "Romance",
  "Fantasy",
  "Science Fiction",
  "Mystery",
  "Thriller",
  "Horror",
  "Historical Fiction",
  "Literary Fiction",
  "Young Adult",
  "Contemporary",
  "Paranormal",
  "Urban Fantasy",
  "Other",
].map((value) => ({ value, label: value }))

const HEAR_ABOUT_OPTIONS = [
  "Social Media",
  "Search Engine",
  "Friend/Family",
  "Blog/Article",
  "Podcast",
  "YouTube",
  "Advertisement",
  "Writing Community",
  "Other",
].map((value) => ({ value, label: value }))

type Option = { value: string; label: string }

/** One dark ReactSelect theme for every dropdown on the stone-900 hero. */
const selectStyles: StylesConfig<Option, false> = {
  control: (base, state) => ({
    ...base,
    backgroundColor: "rgb(250 250 249 / 0.1)",
    borderColor: state.isFocused ? "#f43f5e" : "#57534e",
    borderRadius: "14px",
    minHeight: "48px",
    boxShadow: state.isFocused ? "0 0 0 1px #f43f5e" : "none",
    "&:hover": { borderColor: state.isFocused ? "#f43f5e" : "#78716c" },
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: "#292524",
    border: "1px solid #57534e",
    borderRadius: "14px",
    overflow: "hidden",
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? "#44403c" : state.isSelected ? "#57534e" : "#292524",
    color: "#fafaf9",
    "&:active": { backgroundColor: "#57534e" },
  }),
  singleValue: (base) => ({ ...base, color: "#fafaf9" }),
  input: (base) => ({ ...base, color: "#fafaf9" }),
  placeholder: (base) => ({ ...base, color: "#78716c" }),
}

const fieldClass = cn(
  "min-h-[48px] w-full rounded-[14px] border border-stone-600 bg-stone-50/10 px-4 py-3 font-sans text-[15px] text-stone-50 outline-none placeholder:text-stone-500",
  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rose-500"
)

const labelClass = "text-[13px] font-semibold text-stone-300"

type Step = "email" | "details" | "success"

/**
 * Hero waitlist signup — the original two-step funnel in the refreshed
 * design (ported from WaitlistFormInline):
 *
 *   1. email → public.waitlist row via the www BFF and the SaaS public
 *      waitlist API (+ welcome email w/ referral code; ?ref= referral
 *      attribution honored)
 *   2. details → name, country, genre, hear-about (+ optional phone),
 *      skippable — the email is never lost
 *   3. success → shareable referral link (copy + native share)
 *
 * Returning emails resume where they left off (details incomplete →
 * step 2; complete → straight to their referral link).
 */
export function HeroSignup({
  authorCount,
  authorCountStatus,
  onSignupSuccess,
}: {
  authorCount: number | null
  authorCountStatus: "loading" | "ready" | "error"
  onSignupSuccess?: () => void
}) {
  const [step, setStep] = useState<Step>("email")
  const [email, setEmail] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [genreIdx, setGenreIdx] = useState(0)

  const [waitlistId, setWaitlistId] = useState<string | null>(null)
  const [referralCode, setReferralCode] = useState<string | null>(null)
  const [referredByCode, setReferredByCode] = useState<string | undefined>()
  const [copied, setCopied] = useState(false)

  const [fullName, setFullName] = useState("")
  const [phoneNumber, setPhoneNumber] = useState("")
  const [country, setCountry] = useState("")
  const [genre, setGenre] = useState("")
  const [customGenre, setCustomGenre] = useState("")
  const [hearAbout, setHearAbout] = useState("")
  const [customHearAbout, setCustomHearAbout] = useState("")

  useEffect(() => {
    const timer = setInterval(() => {
      setGenreIdx((idx) => (idx + 1) % GENRES.length)
    }, 2600)
    return () => clearInterval(timer)
  }, [])

  // Referral attribution from ?ref= (and ?email= from invite emails).
  // window.location instead of useSearchParams: enhancement-only, and it
  // keeps the statically prerendered page free of a Suspense boundary.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const refParam = params.get("ref")
    const emailParam = params.get("email")

    if (!refParam) return
    setReferredByCode(refParam)
    if (emailParam) setEmail(decodeURIComponent(emailParam))
  }, [])

  const genreWord = GENRES[genreIdx] ?? GENRES[0]

  async function handleEmailSubmit(e: React.FormEvent) {
    e.preventDefault()
    const trimmed = email.trim().toLowerCase()
    if (!trimmed) return

    setSubmitting(true)
    setError(null)

    try {
      // One request lands the row in the product database (AUT-236). The
      // referral code is the row key for the details step.
      const entry = await joinWaitlist(trimmed, referredByCode)
      setWaitlistId(entry.referralCode)
      setReferralCode(entry.referralCode)

      if (!entry.created) {
        setStep(entry.detailsCompleted ? "success" : "details")
        onSignupSuccess?.()
        return
      }

      try {
        await sendWelcomeEmail(trimmed, undefined, entry.referralCode)
      } catch {
        // Welcome email is best-effort
      }

      onSignupSuccess?.()
      setStep("details")
    } catch {
      setError("Something went wrong. Please try again.")
    } finally {
      setSubmitting(false)
    }
  }

  async function handleDetailsSubmit(e: React.FormEvent) {
    e.preventDefault()

    const finalGenre = genre === "Other" ? customGenre.trim() : genre
    const finalHearAbout = hearAbout === "Other" ? customHearAbout.trim() : hearAbout

    if (!fullName.trim() || !country || !finalGenre || !finalHearAbout) {
      setError("Please fill in your name, country, genre, and how you heard about us.")
      return
    }

    if (!waitlistId) {
      setError("Something went wrong. Please try again.")
      return
    }

    setSubmitting(true)
    setError(null)

    try {
      await completeWaitlistDetails(waitlistId, {
        fullName: fullName.trim(),
        ...(phoneNumber.trim() && { phoneNumber: phoneNumber.trim() }),
        country,
        genre: finalGenre,
        hearAbout: finalHearAbout,
      })
      setStep("success")
    } catch {
      setError("Failed to save details. Please try again.")
    } finally {
      setSubmitting(false)
    }
  }

  const referralLink =
    referralCode && typeof window !== "undefined"
      ? `${window.location.origin}/?ref=${referralCode}`
      : null

  async function copyReferralLink() {
    if (!referralLink) return
    try {
      await navigator.clipboard.writeText(referralLink)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error("Failed to copy referral link:", err)
    }
  }

  async function shareReferralLink() {
    if (!referralLink) return
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Join the Authorythm waitlist",
          text: "I'm on the waitlist for Authorythm — join me:",
          url: referralLink,
        })
      } catch {
        // User dismissed the share sheet
      }
    } else {
      await copyReferralLink()
    }
  }

  return (
    <div className="flex max-w-[620px] flex-col gap-6">
      <span className="font-sans text-xs font-semibold tracking-[0.16em] text-rose-300 uppercase">
        From premise to manuscript
      </span>

      <h1 className="font-display m-0 text-[clamp(2.5rem,5vw,4rem)] leading-[1.15] font-medium tracking-[-0.02em] text-stone-50">
        <span className="block">Master the craft, and the business, of</span>
        <span className="block min-h-[1.15em]">
          <em
            key={genreWord}
            className="inline-block animate-[wordIn_480ms_cubic-bezier(0.22,1,0.36,1)] text-rose-300 italic whitespace-nowrap"
          >
            {genreWord}
          </em>
          .
        </span>
      </h1>

      <p className="m-0 max-w-[54ch] text-lg leading-[1.65] text-pretty text-stone-300">
        Authorythm breaks your book into the components that matter — genre, tone, tropes, heat — then
        guides you through five stages, from Foundation to Content. The wizard does the scaffolding so you
        can write the story.
      </p>

      {step === "email" ? (
        <form
          id="signup"
          onSubmit={handleEmailSubmit}
          className="flex max-w-[480px] flex-col gap-3 sm:flex-row sm:gap-3"
        >
          <input
            type="email"
            name="email"
            required
            autoComplete="email"
            aria-label="Email address"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={submitting}
            className={cn(fieldClass, "flex-1")}
          />
          <button
            type="submit"
            disabled={submitting}
            className="shrink-0 rounded-[14px] bg-rose-600 px-[22px] py-3 text-[15px] font-semibold whitespace-nowrap text-white transition-colors hover:bg-rose-700 hover:shadow-[0_8px_24px_-8px_rgb(225_29_72/0.25)] disabled:opacity-60"
          >
            {submitting ? "Joining…" : "Join the waitlist"}
          </button>
        </form>
      ) : null}

      {step === "details" ? (
        <form
          id="signup"
          onSubmit={handleDetailsSubmit}
          className="flex max-w-[480px] flex-col gap-3.5 rounded-[20px] border border-stone-600 bg-stone-50/5 p-5"
        >
          <div>
            <div className="text-[17px] font-semibold text-stone-50">You're on the list</div>
            <p className="m-0 mt-1 text-[13.5px] leading-relaxed text-stone-400">
              Tell us a little about yourself so we can personalize your invite.
            </p>
          </div>

          <label className="flex flex-col gap-1.5">
            <span className={labelClass}>Full name</span>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Your full name"
              autoComplete="name"
              required
              className={fieldClass}
            />
          </label>

          <label className="flex flex-col gap-1.5">
            <span className={labelClass}>Country of residence</span>
            <ReactSelect<Option>
              options={COUNTRIES}
              value={COUNTRIES.find((c) => c.value === country) || null}
              onChange={(option) => setCountry(option?.value || "")}
              placeholder="Search and select your country…"
              isClearable
              styles={selectStyles}
            />
          </label>

          <label className="flex flex-col gap-1.5">
            <span className={labelClass}>Favorite genre</span>
            <ReactSelect<Option>
              options={GENRE_OPTIONS}
              value={GENRE_OPTIONS.find((g) => g.value === genre) || null}
              onChange={(option) => setGenre(option?.value || "")}
              placeholder="Search and select your favorite genre…"
              isClearable
              styles={selectStyles}
            />
          </label>
          {genre === "Other" ? (
            <input
              type="text"
              value={customGenre}
              onChange={(e) => setCustomGenre(e.target.value)}
              aria-label="Custom genre"
              placeholder="Tell us your genre"
              className={fieldClass}
            />
          ) : null}

          <label className="flex flex-col gap-1.5">
            <span className={labelClass}>How did you hear about us?</span>
            <ReactSelect<Option>
              options={HEAR_ABOUT_OPTIONS}
              value={HEAR_ABOUT_OPTIONS.find((h) => h.value === hearAbout) || null}
              onChange={(option) => setHearAbout(option?.value || "")}
              placeholder="Select one…"
              isClearable
              styles={selectStyles}
            />
          </label>
          {hearAbout === "Other" ? (
            <input
              type="text"
              value={customHearAbout}
              onChange={(e) => setCustomHearAbout(e.target.value)}
              aria-label="How you heard about us"
              placeholder="Where did you find us?"
              className={fieldClass}
            />
          ) : null}

          <label className="flex flex-col gap-1.5">
            <span className={labelClass}>Phone number (optional)</span>
            <input
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="Your phone number"
              autoComplete="tel"
              className={fieldClass}
            />
          </label>

          <div className="mt-1 flex items-center gap-3">
            <button
              type="submit"
              disabled={submitting}
              className="rounded-[14px] bg-rose-600 px-[22px] py-3 text-[15px] font-semibold text-white transition-colors hover:bg-rose-700 hover:shadow-[0_8px_24px_-8px_rgb(225_29_72/0.25)] disabled:opacity-60"
            >
              {submitting ? "Saving…" : "Save details"}
            </button>
            <button
              type="button"
              disabled={submitting}
              onClick={() => setStep("success")}
              className="text-[13.5px] font-semibold text-stone-400 transition-colors hover:text-stone-200"
            >
              I&apos;ll do this later
            </button>
          </div>
        </form>
      ) : null}

      {step === "success" ? (
        <div className="flex max-w-[480px] flex-col gap-3.5 rounded-[20px] border border-stone-600 bg-stone-50/5 p-5">
          <div className="flex items-center gap-2.5 text-[15px] text-stone-200">
            <Check className="size-[18px] shrink-0 text-rose-400" strokeWidth={1.75} aria-hidden />
            You&apos;re on the waitlist — check your inbox for your welcome email.
          </div>
          {referralLink ? (
            <>
              <p className="m-0 text-[13.5px] leading-relaxed text-stone-400">
                Share your link: every author who joins with it and subscribes at launch earns you a bonus.
              </p>
              <div className="flex flex-col gap-2 sm:flex-row">
                <span className="min-w-0 flex-1 truncate rounded-[14px] border border-stone-600 bg-stone-50/10 px-4 py-3 font-mono text-[13px] text-stone-300">
                  {referralLink}
                </span>
                <div className="flex shrink-0 gap-2">
                  <button
                    type="button"
                    onClick={copyReferralLink}
                    aria-label="Copy referral link"
                    className="inline-flex items-center gap-1.5 rounded-[14px] border border-stone-600 px-4 py-3 text-[13.5px] font-semibold text-stone-200 transition-colors hover:border-stone-400"
                  >
                    {copied ? (
                      <Check className="size-[15px] text-rose-400" strokeWidth={2} aria-hidden />
                    ) : (
                      <Copy className="size-[15px]" strokeWidth={1.75} aria-hidden />
                    )}
                    {copied ? "Copied" : "Copy"}
                  </button>
                  <button
                    type="button"
                    onClick={shareReferralLink}
                    aria-label="Share referral link"
                    className="inline-flex items-center gap-1.5 rounded-[14px] bg-rose-600 px-4 py-3 text-[13.5px] font-semibold text-white transition-colors hover:bg-rose-700"
                  >
                    <Share2 className="size-[15px]" strokeWidth={1.75} aria-hidden />
                    Share
                  </button>
                </div>
              </div>
            </>
          ) : null}
        </div>
      ) : null}

      {error ? <p className="m-0 text-sm text-rose-300">{error}</p> : null}

      <span className="text-[13px] text-stone-400">
        {authorCountStatus === "loading"
          ? "Loading author count…"
          : authorCountStatus === "ready" && authorCount !== null
            ? `${authorCount.toLocaleString("en-US")}+ authors are already writing with Authorythm.`
            : "Join authors already writing with Authorythm."}
      </span>
    </div>
  )
}
